import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { setUser } from '../store/slices/authSlice';
import { setLoading, setError, setSuccess } from '../store/slices/uiSlice';
import Loader from '../components/common/loader';

// Mock profile update API since we don't have one in the backend yet
const updateProfile = async (userData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Return success response
  return {
    status: 'success',
    message: 'Profile updated successfully',
    data: {
      user: userData
    }
  };
};

// Validation schema
const profileSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name cannot exceed 50 characters'),
  
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format'),
});

const Profile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { isLoading } = useSelector(state => state.ui);
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      dispatch(setLoading(true));
      
      // Keep the existing user properties and update only name and email
      const updatedUser = {
        ...user,
        name: values.name,
        email: values.email
      };
      
      await updateProfile(updatedUser);
      
      dispatch(setUser(updatedUser));
      dispatch(setSuccess('Profile updated successfully'));
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      dispatch(setError(errorMessage));
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  return (
    <div className="container-md py-8">
      <div className="card">
        <h2 className="text-2xl font-bold mb-6">Your Profile</h2>
        
        {isEditing ? (
          <Formik
            initialValues={{
              name: user?.name || '',
              email: user?.email || ''
            }}
            validationSchema={profileSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="form-control">
                  <label htmlFor="name" className="form-label">Full Name</label>
                  <Field
                    type="text"
                    name="name"
                    id="name"
                    className="form-input"
                  />
                  <ErrorMessage name="name" component="div" className="form-error" />
                </div>
                
                <div className="form-control">
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <Field
                    type="email"
                    name="email"
                    id="email"
                    className="form-input"
                  />
                  <ErrorMessage name="email" component="div" className="form-error" />
                </div>
                
                <div className="form-control">
                  <label className="form-label">Phone Number</label>
                  <div className="form-input bg-gray-100 cursor-not-allowed">
                    {user?.phone || 'Not available'}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Phone number cannot be changed
                  </p>
                </div>
                
                <div className="flex space-x-4 mt-6">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting || isLoading}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <Loader size="sm" color="white" />
                        <span className="ml-2">Saving...</span>
                      </div>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div>
            <div className="mb-6 border-b pb-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Personal Information</h3>
                <button
                  className="text-blue-600 hover:text-blue-800"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Profile
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-600 font-medium">Full Name</p>
                  <p className="text-gray-900">{user?.name || 'Not available'}</p>
                </div>
                
                <div>
                  <p className="text-gray-600 font-medium">User ID</p>
                  <p className="text-gray-900">{user?.userId || 'Not available'}</p>
                </div>
                
                <div>
                  <p className="text-gray-600 font-medium">Email Address</p>
                  <p className="text-gray-900">{user?.email || 'Not available'}</p>
                </div>
                
                <div>
                  <p className="text-gray-600 font-medium">Phone Number</p>
                  <p className="text-gray-900">{user?.phone || 'Not available'}</p>
                </div>
              </div>
            </div>
            
            <div className="mb-6 border-b pb-4">
              <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
              
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Account Status</p>
                  <p className="text-sm text-gray-600">Your account is active</p>
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Active</span>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Account Security</h3>
              
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium">Change Password</p>
                  <p className="text-sm text-gray-600">Update your password regularly for better security</p>
                </div>
                <button className="btn btn-secondary">Reset Password</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;